import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleClearCredentials = () => {
    localStorage.removeItem('apiKey');
    localStorage.removeItem('apiHostname');
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="app">
          <header className="app-header">
            <div className="header-content">
              <h1>Form Pipeline Manager</h1>
              <p className="subtitle">An error occurred</p>
            </div>
          </header>
          <main style={{ padding: '40px 20px' }}>
            <div style={{
              maxWidth: '600px',
              margin: '0 auto',
              background: 'white',
              borderRadius: '8px',
              padding: '32px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}>
              <h2 style={{ color: '#dc3545', marginTop: 0 }}>Something went wrong</h2>
              <p style={{ color: '#666' }}>
                The application encountered an unexpected error. This might be due to an API compatibility issue.
              </p>

              <div style={{
                background: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '16px',
                marginBottom: '24px',
                overflow: 'auto',
              }}>
                <strong style={{ display: 'block', marginBottom: '8px' }}>Error Details:</strong>
                <code style={{ fontSize: '13px', color: '#dc3545' }}>
                  {this.state.error?.message || 'Unknown error'}
                </code>
                {this.state.error?.stack && (
                  <pre style={{
                    fontSize: '11px',
                    color: '#666',
                    marginTop: '12px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}>
                    {this.state.error.stack}
                  </pre>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={this.handleReset}
                  style={{
                    padding: '10px 20px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Try Again
                </button>
                <button
                  onClick={this.handleClearCredentials}
                  style={{
                    padding: '10px 20px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Clear Credentials & Restart
                </button>
              </div>
            </div>
          </main>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
